import React from "react";
import { useLanguage } from "@/i18n/LanguageContext";
import { useTheme } from "@/theme/ThemeContext";
import { Button } from "@/components/ui/button";
import SEO from "@/components/SEO";
import { SubpageNav, SubpageBackLink, SubpageFooter } from "@/components/SubpageLayout";
import {
  TrendingUp, Target, Users, Globe, Microscope, Award, Mail,
  ArrowRight, BarChart3, Shield, Zap, FileText, Building2,
  Database, CheckCircle, Code,
} from "lucide-react";

const TX = {
  bg: {
    seo_title: "Инвеститори",
    seo_desc: "Nutramilo — pre-seed инвестиционна възможност в AI платформа за метаболитна интелигентност и персонализирано хранене.",
    back: "Начало",
    badge: "За инвеститори",
    hero_title: "Преобразяваме как Европа разбира метаболитното си здраве",
    hero_desc: "Nutramilo комбинира клинично валидиран AI, нутригеномика и реална медицинска практика, за да предложи персонализирани здравни прозрения, които превенцията заслужава.",
    cta_deck: "Заявете pitch deck",
    cta_call: "Резервирайте обаждане",
    sections: {
      opportunity: {
        title: "Пазарна възможност",
        items: [
          { stat: "$78B", label: "Глобален пазар на персонализирано здраве (2030)", source: "Grand View Research" },
          { stat: "12.4%", label: "Прогнозен CAGR за digital health AI", source: "McKinsey Health Tech 2025" },
          { stat: "67%", label: "От европейците желаят AI-базирани здравни прозрения", source: "EU Digital Health Survey" },
          { stat: "€42B", label: "Европейски пазар на превентивна медицина", source: "Statista 2024" },
        ],
      },
      product: {
        title: "Продукт и диференциация",
        bullets: [
          "Клинично валидирана AI платформа (81/81 тестове, 4-слойна валидационна рамка)",
          "Експертни AI агенти: EndoBot, NutriBot, NephroBot, HemoBot",
          "Doctor Portal — лекарският paradigm shift към превенция",
          "Patented DIL модел (Dietary Insulin Load) — не само калории",
          "Multi-modal вход: лабораторни PDF-и, ДНК, рентгенови снимки, гласови дневници",
          "Интеграция с реална клинична практика (МДЦ Превентика, София)",
        ],
      },
      traction: {
        title: "Текущи постижения",
        items: [
          { label: "Активни AI модули", value: "27+" },
          { label: "Клинични калкулатори", value: "11" },
          { label: "Метаболитни фенотипи", value: "P1–P5" },
          { label: "Поддържани езици", value: "BG / EN / ES" },
          { label: "Партньорство", value: "1 клиника" },
          { label: "Soft Launch", value: "Q2 2026" },
        ],
      },
      tech: {
        title: "Технологичен стек",
        bullets: [
          "Frontend: React 18, TailwindCSS, shadcn/ui",
          "Backend: FastAPI, MongoDB, Pydantic",
          "AI: GPT-5.2 Vision, Whisper STT, multi-agent оркестрация",
          "Validation: 4-layer framework (math / physiology / inter-rater / population)",
          "Compliance: GDPR, AES-256, hCaptcha, rate limiting, JWT auth",
          "DevOps: CI/CD pipeline, deployment health checks, monitoring",
        ],
      },
      team: {
        title: "Екип и експертиза",
        bullets: [
          "Учредител с 20+ години опит в метаболитна ендокринология",
          "Клиничен консултант: Д-р Лили Иванова (МДЦ Превентика)",
          "AI експертен съвет с публикации в peer-reviewed журнали",
          "Партньор за публикации: International Sci Ink Press Ltd.",
        ],
      },
      moat: {
        title: "Конкурентни предимства",
        bullets: [
          "Българска GLOBOCAN 2024 калибрация (уникална за региона)",
          "Gail/BCRAT хибриден риск модел",
          "Validation framework, готов за peer review публикация",
          "Multilingual, регионално-калибрирани прозрения",
          "Двустранен пазар — пациенти + лекари",
          "Soft moat: Real-world clinician feedback loop (inter-rater study)",
        ],
      },
      use: {
        title: "Използване на средствата",
        items: [
          { pct: "40%", label: "Растеж на потребителската база — performance marketing, B2B продажби в клиники" },
          { pct: "30%", label: "Продуктово развитие — wearables, mobile apps, Longevity Coach AI" },
          { pct: "20%", label: "Научна валидация — peer-reviewed публикации, retrospective кохорти, SDK reference implementations" },
          { pct: "10%", label: "Оперативни разходи и резерв" },
        ],
      },
      ask: {
        title: "Текущ инвестиционен кръг",
        line1: "Pre-seed: €500K – €1.2M",
        line2: "12-месечен runway, цел Series A в Q3 2027",
        line3: "Готови за SAFE или equity (по избор на инвеститора)",
      },
      contact: {
        title: "Контакт за инвеститори",
        email_label: "Email",
        email: "investors@nutramilo.com",
        company: "International Sci Ink Press Ltd.",
        location: "София, България",
        note: "Предпочитаме инвеститори със стратегически опит в digital health, longevity или EU regulatory.",
      },
      docs: {
        title: "Документация",
        items: [
          "Pitch deck (PDF) — по заявка",
          "Financial model (Excel) — под NDA",
          "Научна методология whitepaper (образователна позиция, MDR 2017/745 съображения)",
          "Cap table — под NDA",
        ],
      },
    },
  },
  en: {
    seo_title: "Investors",
    seo_desc: "Nutramilo — pre-seed investment opportunity in AI metabolic intelligence and personalized nutrition platform.",
    back: "Home",
    badge: "For Investors",
    hero_title: "Transforming how Europe understands metabolic health",
    hero_desc: "Nutramilo combines clinically-validated AI, nutrigenomics, and real-world medical practice to deliver personalized health insights that prevention deserves.",
    cta_deck: "Request pitch deck",
    cta_call: "Book a call",
    sections: {
      opportunity: {
        title: "Market opportunity",
        items: [
          { stat: "$78B", label: "Global personalized health market (2030)", source: "Grand View Research" },
          { stat: "12.4%", label: "Forecast CAGR for digital health AI", source: "McKinsey Health Tech 2025" },
          { stat: "67%", label: "Of Europeans want AI-based health insights", source: "EU Digital Health Survey" },
          { stat: "€42B", label: "European preventive medicine market", source: "Statista 2024" },
        ],
      },
      product: {
        title: "Product and differentiation",
        bullets: [
          "Clinically validated AI platform (81/81 tests, 4-layer validation framework)",
          "Expert AI agents: EndoBot, NutriBot, NephroBot, HemoBot",
          "Doctor Portal — the prevention paradigm shift",
          "Patented DIL (Dietary Insulin Load) model — beyond calories",
          "Multi-modal input: lab PDFs, DNA, X-rays, voice journals",
          "Real clinical practice integration (MDC Preventika, Sofia)",
        ],
      },
      traction: {
        title: "Current traction",
        items: [
          { label: "Active AI modules", value: "27+" },
          { label: "Clinical calculators", value: "11" },
          { label: "Metabolic phenotypes", value: "P1–P5" },
          { label: "Supported languages", value: "BG / EN / ES" },
          { label: "Clinical partnerships", value: "1 clinic" },
          { label: "Soft launch", value: "Q2 2026" },
        ],
      },
      tech: {
        title: "Technology stack",
        bullets: [
          "Frontend: React 18, TailwindCSS, shadcn/ui",
          "Backend: FastAPI, MongoDB, Pydantic",
          "AI: GPT-5.2 Vision, Whisper STT, multi-agent orchestration",
          "Validation: 4-layer framework (math / physiology / inter-rater / population)",
          "Compliance: GDPR, AES-256, hCaptcha, rate limiting, JWT auth",
          "DevOps: CI/CD pipeline, deployment health checks, monitoring",
        ],
      },
      team: {
        title: "Team and expertise",
        bullets: [
          "Founder with 20+ years in metabolic endocrinology",
          "Clinical consultant: Dr. Lili Ivanova (MDC Preventika)",
          "AI advisory board with peer-reviewed publications",
          "Publishing partner: International Sci Ink Press Ltd.",
        ],
      },
      moat: {
        title: "Competitive moat",
        bullets: [
          "Bulgaria GLOBOCAN 2024 calibration (regionally unique)",
          "Gail/BCRAT hybrid risk model",
          "Validation framework, peer-review ready",
          "Multilingual, regionally-calibrated insights",
          "Two-sided market — patients + clinicians",
          "Soft moat: Real-world clinician feedback loop (inter-rater study)",
        ],
      },
      use: {
        title: "Use of funds",
        items: [
          { pct: "40%", label: "User base growth — performance marketing, B2B clinic sales" },
          { pct: "30%", label: "Product development — wearables, mobile apps, Longevity Coach AI" },
          { pct: "20%", label: "Scientific validation — peer-reviewed publications, retrospective cohorts, SDK reference implementations" },
          { pct: "10%", label: "Operating expenses and reserve" },
        ],
      },
      ask: {
        title: "Current investment round",
        line1: "Pre-seed: €500K – €1.2M",
        line2: "12-month runway, Series A target Q3 2027",
        line3: "Ready for SAFE or equity (investor's choice)",
      },
      contact: {
        title: "Investor contact",
        email_label: "Email",
        email: "investors@nutramilo.com",
        company: "International Sci Ink Press Ltd.",
        location: "Sofia, Bulgaria",
        note: "We prefer investors with strategic expertise in digital health, longevity, or EU regulatory.",
      },
      docs: {
        title: "Documentation",
        items: [
          "Pitch deck (PDF) — on request",
          "Financial model (Excel) — under NDA",
          "Scientific methodology whitepaper (educational positioning, MDR 2017/745 considerations)",
          "Cap table — under NDA",
        ],
      },
    },
  },
  es: {
    seo_title: "Inversores",
    seo_desc: "Nutramilo — oportunidad de inversión pre-seed en inteligencia metabólica AI y plataforma de nutrición personalizada.",
    back: "Inicio",
    badge: "Para Inversores",
    hero_title: "Transformando cómo Europa comprende la salud metabólica",
    hero_desc: "Nutramilo combina AI validada clínicamente, nutrigenómica y práctica médica del mundo real para ofrecer información de salud personalizada que la prevención merece.",
    cta_deck: "Solicitar pitch deck",
    cta_call: "Reservar una llamada",
    sections: {
      opportunity: {
        title: "Oportunidad de mercado",
        items: [
          { stat: "$78B", label: "Mercado global de salud personalizada (2030)", source: "Grand View Research" },
          { stat: "12.4%", label: "CAGR previsto para AI en salud digital", source: "McKinsey Health Tech 2025" },
          { stat: "67%", label: "De los europeos desean información de salud basada en AI", source: "EU Digital Health Survey" },
          { stat: "€42B", label: "Mercado europeo de medicina preventiva", source: "Statista 2024" },
        ],
      },
      product: {
        title: "Producto y diferenciación",
        bullets: [
          "Plataforma AI validada clínicamente (81/81 pruebas, marco de validación de 4 capas)",
          "Agentes AI expertos: EndoBot, NutriBot, NephroBot, HemoBot",
          "Doctor Portal — el cambio de paradigma en prevención",
          "Modelo DIL (Dietary Insulin Load) patentado — más allá de las calorías",
          "Entrada multimodal: PDFs de laboratorio, ADN, radiografías, diarios de voz",
          "Integración en práctica clínica real (MDC Preventika, Sofía)",
        ],
      },
      traction: {
        title: "Tracción actual",
        items: [
          { label: "Módulos AI activos", value: "27+" },
          { label: "Calculadoras clínicas", value: "11" },
          { label: "Fenotipos metabólicos", value: "P1–P5" },
          { label: "Idiomas soportados", value: "BG / EN / ES" },
          { label: "Alianzas clínicas", value: "1 clínica" },
          { label: "Lanzamiento suave", value: "Q2 2026" },
        ],
      },
      tech: {
        title: "Stack tecnológico",
        bullets: [
          "Frontend: React 18, TailwindCSS, shadcn/ui",
          "Backend: FastAPI, MongoDB, Pydantic",
          "AI: GPT-5.2 Vision, Whisper STT, orquestación multiagente",
          "Validación: marco de 4 capas (matemática / fisiología / inter-evaluadores / población)",
          "Cumplimiento: GDPR, AES-256, hCaptcha, limitación de tasa, autenticación JWT",
          "DevOps: pipeline CI/CD, verificaciones de salud del despliegue, monitorización",
        ],
      },
      team: {
        title: "Equipo y experiencia",
        bullets: [
          "Fundador con más de 20 años en endocrinología metabólica",
          "Consultora clínica: Dr. Lili Ivanova (MDC Preventika)",
          "Consejo asesor de AI con publicaciones revisadas por pares",
          "Socio editorial: International Sci Ink Press Ltd.",
        ],
      },
      moat: {
        title: "Ventaja competitiva",
        bullets: [
          "Calibración Bulgaria GLOBOCAN 2024 (única a nivel regional)",
          "Modelo híbrido de riesgo Gail/BCRAT",
          "Marco de validación, listo para revisión por pares",
          "Información multilingüe calibrada regionalmente",
          "Mercado bilateral — pacientes + médicos",
          "Ventaja blanda: bucle de retroalimentación de médicos del mundo real (estudio inter-evaluadores)",
        ],
      },
      use: {
        title: "Uso de los fondos",
        items: [
          { pct: "40%", label: "Crecimiento de la base de usuarios — marketing de rendimiento, ventas B2B a clínicas" },
          { pct: "30%", label: "Desarrollo de producto — wearables, aplicaciones móviles, Longevity Coach AI" },
          { pct: "20%", label: "Validación científica — publicaciones revisadas por pares, cohortes retrospectivas, implementaciones de referencia del SDK" },
          { pct: "10%", label: "Gastos operativos y reserva" },
        ],
      },
      ask: {
        title: "Ronda de inversión actual",
        line1: "Pre-seed: €500K – €1.2M",
        line2: "Pista de 12 meses, objetivo Series A en Q3 2027",
        line3: "Preparados para SAFE o capital (a elección del inversor)",
      },
      contact: {
        title: "Contacto para inversores",
        email_label: "Correo electrónico",
        email: "investors@nutramilo.com",
        company: "International Sci Ink Press Ltd.",
        location: "Sofía, Bulgaria",
        note: "Preferimos inversores con experiencia estratégica en salud digital, longevidad o regulación de la UE.",
      },
      docs: {
        title: "Documentación",
        items: [
          "Pitch deck (PDF) — bajo solicitud",
          "Modelo financiero (Excel) — bajo NDA",
          "Documento técnico de metodología científica (posicionamiento educativo, consideraciones MDR 2017/745)",
          "Tabla de capitalización — bajo NDA",
        ],
      },
    },
  },
};

export default function InvestorsPage() {
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const t = TX[language] || TX.en;
  const bg = language === "bg";

  const sectionBg = isDark ? "bg-[hsl(223,47%,6%)]" : "bg-[#F9F9F7]";
  const altBg = isDark ? "bg-[hsl(223,47%,8%)]" : "bg-white";
  const heading = `text-2xl sm:text-3xl font-bold tracking-tight ${isDark ? "text-white" : "text-[#1A1A2E]"}`;
  const subheading = `text-lg font-bold ${isDark ? "text-white" : "text-[#1A1A2E]"}`;
  const body = `text-sm leading-relaxed ${isDark ? "text-slate-300" : "text-slate-600"}`;
  const badgeCls = `inline-flex items-center gap-2 px-3 py-1 rounded-full border text-xs font-medium ${isDark ? "border-[hsl(223,47%,20%)] text-slate-400" : "border-slate-200 text-slate-500"}`;

  return (
    <div className={`min-h-screen ${sectionBg}`} data-testid="investors-page" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
      <SEO title={t.seo_title} description={t.seo_desc} path="/investors" />
      <SubpageNav />

      {/* Hero */}
      <section className={`relative overflow-hidden ${altBg}`}>
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: "radial-gradient(circle at 1px 1px, currentColor 1px, transparent 0)", backgroundSize: "40px 40px" }} />
        <div className="max-w-4xl mx-auto px-4 py-16 sm:py-24 relative">
          <SubpageBackLink label={t.back} />
          <div className={badgeCls}><TrendingUp className="w-3.5 h-3.5" /> {t.badge}</div>
          <h1 className={`text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mt-4 mb-5 leading-tight ${isDark ? "text-white" : "text-[#1A1A2E]"}`} style={{ fontFamily: "'Work Sans', sans-serif" }}>
            {t.hero_title}
          </h1>
          <p className={`text-base sm:text-lg leading-relaxed mb-8 max-w-2xl ${isDark ? "text-slate-300" : "text-slate-600"}`}>
            {t.hero_desc}
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <Button asChild size="lg" data-testid="investors-deck-cta" className="bg-gradient-to-r from-[#0EA5E9] to-[#6366F1] hover:opacity-90 text-white text-sm font-semibold rounded-full px-8 h-12 shadow-lg shadow-sky-500/20">
              <a href="mailto:investors@nutramilo.com?subject=Nutramilo%20pitch%20deck%20request">
                {t.cta_deck} <ArrowRight className="w-4 h-4 ml-2" />
              </a>
            </Button>
            <Button asChild variant="outline" size="lg" data-testid="investors-call-cta" className={`rounded-full px-8 h-12 text-sm font-medium ${isDark ? "border-slate-700 text-slate-300" : "border-slate-200 text-slate-600"}`}>
              <a href="mailto:investors@nutramilo.com?subject=Nutramilo%20investor%20call">
                {t.cta_call}
              </a>
            </Button>
          </div>
        </div>
      </section>

      {/* Market opportunity */}
      <section className={`py-14 ${sectionBg}`}>
        <div className="max-w-5xl mx-auto px-4">
          <div className="flex items-center gap-3 mb-8">
            <Globe className="w-6 h-6" style={{ color: "#0EA5E9" }} />
            <h2 className={heading}>{t.sections.opportunity.title}</h2>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="investors-market-grid">
            {t.sections.opportunity.items.map((it) => (
              <div key={it.label} className={`p-5 rounded-2xl border ${isDark ? "bg-[hsl(223,47%,10%)] border-[hsl(223,47%,16%)]" : "bg-white border-slate-100 shadow-sm"}`}>
                <p className="text-3xl font-bold tracking-tight mb-1" style={{ color: "#0EA5E9" }}>{it.stat}</p>
                <p className={`text-sm font-medium mb-1 ${isDark ? "text-slate-200" : "text-slate-700"}`}>{it.label}</p>
                <p className={`text-[10px] ${isDark ? "text-slate-500" : "text-slate-400"}`}>{it.source}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Product + Tech + Team — three-column block */}
      <section className={`py-14 ${altBg}`}>
        <div className="max-w-5xl mx-auto px-4 grid grid-cols-1 lg:grid-cols-3 gap-6">
          {[
            { key: "product", icon: Target, color: "#8B5CF6" },
            { key: "tech", icon: Zap, color: "#10B981" },
            { key: "team", icon: Users, color: "#F59E0B" },
          ].map(({ key, icon: Icon, color }) => (
            <div key={key} className={`p-6 rounded-2xl border ${isDark ? "bg-[hsl(223,47%,10%)] border-[hsl(223,47%,16%)]" : "bg-white border-slate-100 shadow-sm"}`} data-testid={`investors-${key}-card`}>
              <Icon className="w-6 h-6 mb-3" style={{ color }} />
              <h3 className={`${subheading} mb-3`}>{t.sections[key].title}</h3>
              <ul className="space-y-2">
                {t.sections[key].bullets.map((b) => (
                  <li key={b} className="flex items-start gap-2">
                    <ArrowRight className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" style={{ color }} />
                    <span className={body}>{b}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      {/* Traction */}
      <section className={`py-14 ${sectionBg}`}>
        <div className="max-w-5xl mx-auto px-4">
          <div className="flex items-center gap-3 mb-8">
            <BarChart3 className="w-6 h-6" style={{ color: "#10B981" }} />
            <h2 className={heading}>{t.sections.traction.title}</h2>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4" data-testid="investors-traction-grid">
            {t.sections.traction.items.map((it) => (
              <div key={it.label} className={`p-5 rounded-2xl border text-center ${isDark ? "bg-[hsl(223,47%,10%)] border-[hsl(223,47%,16%)]" : "bg-white border-slate-100 shadow-sm"}`}>
                <p className="text-2xl font-bold mb-1 text-[#10B981]">{it.value}</p>
                <p className={`text-xs ${isDark ? "text-slate-400" : "text-slate-500"}`}>{it.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Moat */}
      <section className={`py-14 ${altBg}`}>
        <div className="max-w-4xl mx-auto px-4">
          <div className="flex items-center gap-3 mb-8">
            <Shield className="w-6 h-6" style={{ color: "#EF4444" }} />
            <h2 className={heading}>{t.sections.moat.title}</h2>
          </div>
          <ul className="space-y-3" data-testid="investors-moat-list">
            {t.sections.moat.bullets.map((m) => (
              <li key={m} className={`flex items-start gap-3 p-4 rounded-xl border ${isDark ? "bg-[hsl(223,47%,10%)] border-[hsl(223,47%,16%)]" : "bg-white border-slate-100"}`}>
                <Award className="w-4 h-4 mt-0.5 flex-shrink-0 text-[#EF4444]" />
                <span className={`text-sm ${isDark ? "text-slate-200" : "text-slate-700"}`}>{m}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* iter350.C — Verifiable Science (defensible IP / open SDK) */}
      <section className={`py-14 ${sectionBg}`} data-testid="investors-verifiable-science">
        <div className="max-w-4xl mx-auto px-4">
          <div className="flex items-center gap-3 mb-3">
            <Code className="w-6 h-6 text-emerald-500" />
            <h2 className={heading}>
              {bg ? "Доказуема наука — pip install ours" : "Verifiable Science — pip install ours"}
            </h2>
          </div>
          <p className={`${body} mb-6 max-w-2xl`}>
            {bg
              ? "Докато MyFitnessPal и Cronometer крият формулите си, нашите са public, citation-backed, deterministic — peer-reviewed Python SDK, инсталируем с една команда. Това е защитим IP moat, който инвеститорите могат да валидират в реално време."
              : "While MyFitnessPal and Cronometer hide their formulas, ours are public, citation-backed, deterministic — a peer-reviewed Python SDK, installable with one command. This is defensible IP moat that investors can verify in real time."}
          </p>

          {/* Trust grid */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
            <div
              className={`rounded-xl p-4 border ${isDark ? "bg-[hsl(223,47%,10%)] border-emerald-700/30" : "bg-white border-emerald-200"}`}
              data-testid="investors-science-trust-1"
            >
              <CheckCircle className="w-5 h-5 text-emerald-500 mb-2" />
              <p className={`text-xs font-bold mb-1 ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>
                {bg ? "102/102 преминати теста" : "102/102 tests passing"}
              </p>
              <p className={`text-[11px] ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                {bg
                  ? "Pytest регресии за NIS, DIL, UIS, IL ceiling и SDK експортите."
                  : "Pytest regressions for NIS, DIL, UIS, IL ceiling and SDK exports."}
              </p>
            </div>
            <div
              className={`rounded-xl p-4 border ${isDark ? "bg-[hsl(223,47%,10%)] border-cyan-700/30" : "bg-white border-cyan-200"}`}
              data-testid="investors-science-trust-2"
            >
              <Database className="w-5 h-5 text-cyan-500 mb-2" />
              <p className={`text-xs font-bold mb-1 ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>
                {bg ? "n=147 reference dataset" : "n=147 reference dataset"}
              </p>
              <p className={`text-[11px] ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                {bg
                  ? "Reproducible Colab notebook — Holt 1997 + Bell 2014 + Bao 2009."
                  : "Reproducible Colab notebook — Holt 1997 + Bell 2014 + Bao 2009."}
              </p>
            </div>
            <div
              className={`rounded-xl p-4 border ${isDark ? "bg-[hsl(223,47%,10%)] border-violet-700/30" : "bg-white border-violet-200"}`}
              data-testid="investors-science-trust-3"
            >
              <Shield className="w-5 h-5 text-violet-500 mb-2" />
              <p className={`text-xs font-bold mb-1 ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>
                {bg ? "EU AI Act Чл. 50 готов" : "EU AI Act Art. 50 ready"}
              </p>
              <p className={`text-[11px] ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                {bg
                  ? "Детерминистични правила, без opaque ML — пълна прозрачност."
                  : "Deterministic rules, no opaque ML — full transparency."}
              </p>
            </div>
          </div>

          {/* Terminal install snippet */}
          <div
            className={`rounded-2xl border overflow-hidden ${isDark ? "bg-[hsl(223,47%,8%)] border-[hsl(223,47%,15%)]" : "bg-[#0F1117] border-[#1A1A2E]"}`}
            data-testid="investors-install-snippet"
          >
            <div className="flex items-center justify-between px-4 py-2 border-b border-white/10">
              <div className="flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full bg-red-500/80" />
                <span className="w-2.5 h-2.5 rounded-full bg-yellow-500/80" />
                <span className="w-2.5 h-2.5 rounded-full bg-emerald-500/80" />
                <span className="ml-2 text-[11px] text-slate-500 font-mono">terminal</span>
              </div>
              <a
                href="https://pypi.org/project/nutramilo-nis/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[11px] font-medium text-emerald-400 hover:text-emerald-300 transition-colors"
                data-testid="investors-pypi-link"
              >
                {bg ? "PyPI ↗" : "PyPI ↗"}
              </a>
            </div>
            <pre className="px-4 py-3 overflow-x-auto">
              <code className="text-[12px] sm:text-[13px] leading-relaxed font-mono text-emerald-300">
                <span className="text-slate-500">$</span> pip install nutramilo-nis
              </code>
            </pre>
          </div>

          <p className={`text-[11px] mt-3 italic ${isDark ? "text-slate-500" : "text-slate-400"}`}>
            {bg
              ? "MIT License · pypi.org/project/nutramilo-nis · 30+ peer-reviewed PMID цитата · 102/102 валидации преминават."
              : "MIT License · pypi.org/project/nutramilo-nis · 30+ peer-reviewed PMID citations · 102/102 validations passing."}
          </p>
          {/* iter539 — Zenodo DOI badge */}
          <a
            href="https://doi.org/10.5281/zenodo.20571802"
            target="_blank"
            rel="noopener noreferrer"
            className={`inline-flex items-center gap-1.5 mt-2 text-[11px] font-mono ${
              isDark ? "text-cyan-300 hover:text-cyan-200" : "text-cyan-700 hover:text-cyan-800"
            }`}
            data-testid="investors-zenodo-doi"
          >
            <span className={`px-1.5 py-0.5 rounded ${isDark ? "bg-cyan-500/15" : "bg-cyan-50"}`}>DOI</span>
            10.5281/zenodo.20571802 ↗
          </a>
        </div>
      </section>

      {/* Use of funds */}
      <section className={`py-14 ${sectionBg}`}>
        <div className="max-w-4xl mx-auto px-4">
          <div className="flex items-center gap-3 mb-8">
            <Microscope className="w-6 h-6" style={{ color: "#6366F1" }} />
            <h2 className={heading}>{t.sections.use.title}</h2>
          </div>
          <div className="space-y-2" data-testid="investors-use-list">
            {t.sections.use.items.map((it) => (
              <div key={it.label} className={`flex items-start gap-4 p-4 rounded-xl border ${isDark ? "bg-[hsl(223,47%,10%)] border-[hsl(223,47%,16%)]" : "bg-white border-slate-100"}`}>
                <div className="text-2xl font-bold tracking-tight w-16 flex-shrink-0" style={{ color: "#6366F1" }}>{it.pct}</div>
                <div className={`text-sm ${isDark ? "text-slate-200" : "text-slate-700"}`}>{it.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Ask + Documentation */}
      <section className={`py-14 ${altBg}`}>
        <div className="max-w-5xl mx-auto px-4 grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className={`p-6 rounded-2xl border-2 ${isDark ? "bg-gradient-to-br from-[#0EA5E9]/10 to-[#6366F1]/10 border-[#0EA5E9]/30" : "bg-gradient-to-br from-sky-50 to-indigo-50 border-sky-200"}`} data-testid="investors-ask-card">
            <Building2 className="w-6 h-6 mb-3 text-[#0EA5E9]" />
            <h3 className={`${subheading} mb-3`}>{t.sections.ask.title}</h3>
            <p className={`text-base font-bold mb-1 ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>{t.sections.ask.line1}</p>
            <p className={body}>{t.sections.ask.line2}</p>
            <p className={body}>{t.sections.ask.line3}</p>
          </div>
          <div className={`p-6 rounded-2xl border ${isDark ? "bg-[hsl(223,47%,10%)] border-[hsl(223,47%,16%)]" : "bg-white border-slate-100 shadow-sm"}`} data-testid="investors-docs-card">
            <FileText className="w-6 h-6 mb-3 text-[#F59E0B]" />
            <h3 className={`${subheading} mb-3`}>{t.sections.docs.title}</h3>
            <ul className="space-y-2">
              {t.sections.docs.items.map((d) => (
                <li key={d} className="flex items-start gap-2">
                  <ArrowRight className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-[#F59E0B]" />
                  <span className={body}>{d}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Contact */}
      <section className={`py-14 ${sectionBg}`}>
        <div className="max-w-3xl mx-auto px-4 text-center">
          <Mail className="w-8 h-8 mx-auto mb-4 text-[#0EA5E9]" />
          <h2 className={`${heading} mb-3`}>{t.sections.contact.title}</h2>
          <a href={`mailto:${t.sections.contact.email}`} data-testid="investors-email-link"
             className="text-lg font-semibold text-[#0EA5E9] hover:underline block mb-2">
            {t.sections.contact.email}
          </a>
          <p className={`text-sm ${isDark ? "text-slate-400" : "text-slate-500"}`}>
            {t.sections.contact.company} · {t.sections.contact.location}
          </p>
          <p className={`text-xs mt-4 max-w-xl mx-auto ${isDark ? "text-slate-500" : "text-slate-400"}`}>
            {t.sections.contact.note}
          </p>
        </div>
      </section>

      <SubpageFooter />
    </div>
  );
}
